import React, { useEffect, useState } from 'react';
import {
  AddBoxOutlined as AddBoxOutlinedIcon,
  IosShare as IosShareIcon,
  MoreHoriz as MoreHorizIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  Link as MuiLink,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { gql, useMutation, useQuery } from 'urql';
import { useFlashMessageContext } from '../../contexts/flashMessage';
import {
  FetchMyNotificationPreferencesQuery,
  FetchMyNotificationPreferencesQueryVariables,
  FetchMyPushSubscriptionsQuery,
  FetchMyPushSubscriptionsQueryVariables,
  RegisterPushSubscriptionMutation,
  RegisterPushSubscriptionMutationVariables,
  SendTestPushMutation,
  SendTestPushMutationVariables,
  SetAllNotificationPreferencesMutation,
  SetAllNotificationPreferencesMutationVariables,
  SetNotificationPreferenceMutation,
  SetNotificationPreferenceMutationVariables,
  SetNotifyOwnActionsMutation,
  SetNotifyOwnActionsMutationVariables,
  UnregisterPushSubscriptionMutation,
  UnregisterPushSubscriptionMutationVariables,
} from '../../generated/hooks';
import { MyNotificationPreferences } from '../../generated/schemas';
import {
  fireLocalTestNotification,
  getIosOnboardingState,
  IosOnboardingState,
  PUSH_SUPPORTED,
  subscribeToPush,
} from '../../utils/pushClient';

const PREFERENCES_FIELDS = gql`
  fragment PreferencesFields on MyNotificationPreferences {
    notifyOwnActions
    pushRegistered
    categories {
      category
      displayName
      description
      cadence
      leagueType
      configurable
      pushEnabled
      emailEnabled
    }
  }
`;

const fetchMyNotificationPreferences = gql`
  query fetchMyNotificationPreferences {
    vapidPublicKey
    myNotificationPreferences {
      ...PreferencesFields
    }
  }
  ${PREFERENCES_FIELDS}
`;

const fetchMyPushSubscriptions = gql`
  query fetchMyPushSubscriptions {
    myPushSubscriptions {
      id
      endpoint
      userAgent
      created
      lastUsed
    }
  }
`;

const setNotificationPreferenceMutation = gql`
  mutation setNotificationPreference($category: String!, $channel: String!, $enabled: Boolean!) {
    setNotificationPreference(category: $category, channel: $channel, enabled: $enabled) {
      preferences {
        ...PreferencesFields
      }
    }
  }
  ${PREFERENCES_FIELDS}
`;

const setAllNotificationPreferencesMutation = gql`
  mutation setAllNotificationPreferences($channel: String!, $enabled: Boolean!) {
    setAllNotificationPreferences(channel: $channel, enabled: $enabled) {
      preferences {
        ...PreferencesFields
      }
    }
  }
  ${PREFERENCES_FIELDS}
`;

const setNotifyOwnActionsMutation = gql`
  mutation setNotifyOwnActions($enabled: Boolean!) {
    setNotifyOwnActions(enabled: $enabled) {
      preferences {
        ...PreferencesFields
      }
    }
  }
  ${PREFERENCES_FIELDS}
`;

const registerPushSubscriptionMutation = gql`
  mutation registerPushSubscription($endpoint: String!, $keys: PushSubscriptionKeysInput!, $userAgent: String) {
    registerPushSubscription(endpoint: $endpoint, keys: $keys, userAgent: $userAgent) {
      preferences {
        ...PreferencesFields
      }
    }
  }
  ${PREFERENCES_FIELDS}
`;

const unregisterPushSubscriptionMutation = gql`
  mutation unregisterPushSubscription($endpoint: String!) {
    unregisterPushSubscription(endpoint: $endpoint) {
      ok
    }
  }
`;

const sendTestPushMutation = gql`
  mutation sendTestPush {
    sendTestPush {
      ok
      results {
        endpoint
        success
        pruned
        error
      }
    }
  }
`;

type Prefs = MyNotificationPreferences;
const CHANNELS = ['PUSH', 'EMAIL'] as const;
type Channel = typeof CHANNELS[number];

// Categories are grouped by league type in the grid so they read clearly. `null` leagueType covers
// events that apply to any league (invitations, season results).
const CATEGORY_GROUPS: { key: string | null; label: string }[] = [
  { key: 'STOCK_MARKET', label: 'Stock Market' },
  { key: 'PICK_EM', label: "Pick 'Em" },
  { key: null, label: 'General' },
];

export default function NotificationPreferences() {
  const { addFlashMessage } = useFlashMessageContext();
  const [{ data, fetching }] = useQuery<
    FetchMyNotificationPreferencesQuery,
    FetchMyNotificationPreferencesQueryVariables
  >({ query: fetchMyNotificationPreferences });
  const [{ data: deviceData }, refetchDevices] = useQuery<
    FetchMyPushSubscriptionsQuery,
    FetchMyPushSubscriptionsQueryVariables
  >({ query: fetchMyPushSubscriptions });

  const [, setPreference] = useMutation<SetNotificationPreferenceMutation, SetNotificationPreferenceMutationVariables>(
    setNotificationPreferenceMutation,
  );
  const [, setAllPreferences] = useMutation<
    SetAllNotificationPreferencesMutation,
    SetAllNotificationPreferencesMutationVariables
  >(setAllNotificationPreferencesMutation);
  const [, setNotifyOwnActions] = useMutation<SetNotifyOwnActionsMutation, SetNotifyOwnActionsMutationVariables>(
    setNotifyOwnActionsMutation,
  );
  const [, registerPush] = useMutation<RegisterPushSubscriptionMutation, RegisterPushSubscriptionMutationVariables>(
    registerPushSubscriptionMutation,
  );
  const [, unregisterPush] = useMutation<
    UnregisterPushSubscriptionMutation,
    UnregisterPushSubscriptionMutationVariables
  >(unregisterPushSubscriptionMutation);
  const [, sendTestPush] = useMutation<SendTestPushMutation, SendTestPushMutationVariables>(sendTestPushMutation);

  // Local source of truth, seeded from the query and refreshed from every mutation's returned matrix
  // (MyNotificationPreferences has no id, so graphcache can't normalize it -- we drive it by hand).
  const [prefs, setPrefs] = useState<Prefs | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (data?.myNotificationPreferences) setPrefs(data.myNotificationPreferences);
  }, [data]);

  const vapidPublicKey = data?.vapidPublicKey ?? null;

  const onError = (error: unknown) =>
    addFlashMessage('error', error instanceof Error ? error.message : 'An unknown error occurred');

  const handleToggle = async (category: string, channel: Channel, enabled: boolean) => {
    setBusy(true);
    try {
      const result = await setPreference({ category, channel, enabled });
      const updated = result.data?.setNotificationPreference?.preferences;
      if (updated) setPrefs(updated as Prefs);
      else if (result.error) onError(result.error);
    } finally {
      setBusy(false);
    }
  };

  const handleColumnAll = async (channel: Channel, enabled: boolean) => {
    setBusy(true);
    try {
      const result = await setAllPreferences({ channel, enabled });
      const updated = result.data?.setAllNotificationPreferences?.preferences;
      if (updated) setPrefs(updated as Prefs);
      else if (result.error) onError(result.error);
    } finally {
      setBusy(false);
    }
  };

  const handleNotifyOwnActions = async (enabled: boolean) => {
    const result = await setNotifyOwnActions({ enabled });
    const updated = result.data?.setNotifyOwnActions?.preferences;
    if (updated) setPrefs(updated as Prefs);
    else if (result.error) onError(result.error);
  };

  const handleEnablePush = async () => {
    if (!vapidPublicKey) {
      addFlashMessage('error', 'Push is not configured on the server yet.');
      return;
    }
    setBusy(true);
    try {
      const payload = await subscribeToPush(vapidPublicKey);
      const result = await registerPush({
        endpoint: payload.endpoint,
        keys: payload.keys,
        userAgent: payload.userAgent,
      });
      const updated = result.data?.registerPushSubscription?.preferences;
      if (updated) setPrefs(updated as Prefs);
      refetchDevices({ requestPolicy: 'network-only' });
      // Confirmation beats a silent success -- fire the local display test immediately so the user
      // sees a notification land right after granting permission, instead of wondering if it worked.
      try {
        await fireLocalTestNotification();
        addFlashMessage('success', "✅ You're all set! Notifications are enabled on this device.");
      } catch {
        addFlashMessage('success', 'Push notifications enabled on this device.');
      }
    } catch (error) {
      onError(error);
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveDevice = async (endpoint: string) => {
    const result = await unregisterPush({ endpoint });
    if (result.error) onError(result.error);
    else refetchDevices({ requestPolicy: 'network-only' });
  };

  const handleTestPush = async () => {
    const result = await sendTestPush({});
    if (result.error) return onError(result.error);
    const results = result.data?.sendTestPush?.results ?? [];
    const delivered = results.filter(r => r.success).length;
    addFlashMessage(
      delivered > 0 ? 'success' : 'error',
      `Test push: ${delivered}/${results.length} device(s) reached.`,
    );
    refetchDevices({ requestPolicy: 'network-only' });
  };

  const localTest = async () => {
    try {
      await fireLocalTestNotification();
    } catch (error) {
      onError(error);
    }
  };

  // Only user-configurable categories are shown -- locked ones (e.g. Announcements) always send and
  // aren't rendered here. Grouped by league type for readability.
  const configurableCategories = (prefs?.categories ?? []).filter(c => c.configurable);
  const groupedCategories = CATEGORY_GROUPS.map(group => ({
    ...group,
    rows: configurableCategories.filter(c => (c.leagueType ?? null) === group.key),
  })).filter(group => group.rows.length > 0);
  const pushRegistered = prefs?.pushRegistered ?? false;
  // Live browser state (permission, display-mode, UA), not React state -- recomputed each render
  // rather than memoized so it picks up a permission grant/denial without a page reload.
  const iosState: IosOnboardingState = getIosOnboardingState();
  const permissionDenied = typeof Notification !== 'undefined' && Notification.permission === 'denied';
  // Denied overrides everything else -- a blocked permission can't be re-requested from the page,
  // so the recovery instructions take priority over any iOS-specific guidance.
  const canAttemptEnable =
    PUSH_SUPPORTED &&
    !permissionDenied &&
    iosState !== 'unsupported-version' &&
    iosState !== 'needs-safari' &&
    iosState !== 'needs-install';

  if (fetching && !prefs) {
    return (
      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" sx={{ py: 2 }}>
        <Typography variant="h4">Notification Preferences</Typography>
        <MuiLink component={RouterLink} to="/notifications" underline="hover" variant="body2">
          &larr; Back to Notifications
        </MuiLink>
      </Stack>
      <Stack spacing={2} sx={{ maxWidth: 820, mx: 'auto', pb: 4 }}>
        {/* Devices / push enablement (the subscribe chain is Phase 0 -- deploy/installed-PWA only). */}
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            This device
          </Typography>
          {!PUSH_SUPPORTED && (
            <Alert severity="info" sx={{ mb: 1 }}>
              This browser doesn&apos;t support push notifications. You&apos;ll still receive email.
            </Alert>
          )}
          {permissionDenied && (
            <Alert severity="error" sx={{ mb: 1 }}>
              <strong>Notifications are blocked.</strong> Open iPhone{' '}
              <strong>Settings → Notifications → College Pick Em → Allow Notifications</strong> (or the equivalent for
              this browser), or delete and reinstall the home-screen app, then try again.
            </Alert>
          )}
          {!permissionDenied && iosState === 'unsupported-version' && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              <strong>Notifications need iOS 16.4 or later.</strong> Update iOS to enable push, or keep getting updates
              by email.
            </Alert>
          )}
          {!permissionDenied && iosState === 'needs-safari' && (
            <Alert severity="info" sx={{ mb: 1 }}>
              <strong>Open this page in Safari to install</strong>
              <br />
              On iPhone, notifications require installing the app from <strong>Safari</strong>. Open this link in
              Safari, then follow the install steps.
            </Alert>
          )}
          {!permissionDenied && iosState === 'needs-install' && (
            <Alert severity="info" sx={{ mb: 1 }}>
              <strong>📲 Install the app to get notifications</strong>
              <br />
              On iPhone, notifications only work from the home-screen app. It takes 10 seconds:
              <ol style={{ margin: '4px 0', paddingLeft: '1.25em' }}>
                <li>
                  Tap the <strong>•••</strong> button in the bottom-right corner of Safari&apos;s toolbar.
                </li>
                <li>
                  Tap <IosShareIcon fontSize="small" sx={{ verticalAlign: 'text-bottom' }} /> <strong>Share</strong> .
                </li>
                <li>
                  Scroll down and tap <AddBoxOutlinedIcon fontSize="small" sx={{ verticalAlign: 'text-bottom' }} />{' '}
                  <strong>Add to Home Screen</strong>.
                </li>
                <li>
                  Tap <strong>Add</strong> in the top-right corner.
                </li>
              </ol>
              Then open <strong>College Pick Em</strong> from your home screen and turn on notifications.
            </Alert>
          )}
          {!permissionDenied && iosState === 'needs-permission' && (
            <Alert severity="info" sx={{ mb: 1 }}>
              <strong>🔔 Turn on notifications</strong>
              <br />
              Get pinged the moment your orders fill, dividends pay, and the auction resolves.
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Button variant="contained" disabled={!canAttemptEnable || busy} onClick={handleEnablePush}>
              {iosState === 'needs-permission' ? 'Enable notifications' : 'Enable push on this device'}
            </Button>
            <Button variant="outlined" disabled={!PUSH_SUPPORTED} onClick={localTest}>
              Test display (local)
            </Button>
            <Button variant="outlined" disabled={!pushRegistered} onClick={handleTestPush}>
              Test push (all devices)
            </Button>
          </Box>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>
            Registered devices
          </Typography>
          {(deviceData?.myPushSubscriptions ?? []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No devices registered yet.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {deviceData?.myPushSubscriptions?.map(device => (
                <Stack key={device.id} direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {device.userAgent || device.endpoint}
                  </Typography>
                  <Button size="small" color="error" onClick={() => handleRemoveDevice(device.endpoint)}>
                    Remove
                  </Button>
                </Stack>
              ))}
            </Stack>
          )}
        </Paper>

        {/* Cross-cutting own-action toggle (§2.1). */}
        <Paper sx={{ p: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={prefs?.notifyOwnActions ?? false}
                onChange={e => handleNotifyOwnActions(e.target.checked)}
              />
            }
            label="Notify me about my own actions (e.g. when an order I just placed fills)"
          />
        </Paper>

        {/* The category x channel matrix (§5.5). */}
        <Paper sx={{ p: 2, overflowX: 'auto' }}>
          <Typography variant="h6" gutterBottom>
            Categories
          </Typography>
          {!pushRegistered && (
            <Alert severity="info" sx={{ mb: 1 }}>
              Register a device above to enable push. Until then, notifications are delivered by email.
            </Alert>
          )}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Category</TableCell>
                {CHANNELS.map(channel => (
                  <TableCell key={channel} align="center">
                    <Stack alignItems="center" spacing={0.5}>
                      <span>{channel === 'PUSH' ? 'Push' : 'Email'}</span>
                      <Box>
                        <Button
                          size="small"
                          disabled={busy || (channel === 'PUSH' && !pushRegistered)}
                          onClick={() => handleColumnAll(channel, true)}
                        >
                          All
                        </Button>
                        <Button
                          size="small"
                          disabled={busy || (channel === 'PUSH' && !pushRegistered)}
                          onClick={() => handleColumnAll(channel, false)}
                        >
                          None
                        </Button>
                      </Box>
                    </Stack>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {groupedCategories.map(group => (
                <React.Fragment key={group.label}>
                  <TableRow>
                    <TableCell
                      colSpan={CHANNELS.length + 1}
                      sx={{ bgcolor: 'action.hover', fontWeight: 600, color: 'text.secondary' }}
                    >
                      {group.label}
                    </TableCell>
                  </TableRow>
                  {group.rows.map(row => (
                    <TableRow key={row.category}>
                      <TableCell>
                        <Typography variant="body2">{row.displayName}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {row.description}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Switch
                          size="small"
                          checked={row.pushEnabled}
                          disabled={!pushRegistered || busy}
                          onChange={e => handleToggle(row.category, 'PUSH', e.target.checked)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Switch
                          size="small"
                          checked={row.emailEnabled}
                          disabled={busy}
                          onChange={e => handleToggle(row.category, 'EMAIL', e.target.checked)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Stack>
    </>
  );
}
